/**
 * AI Co-Pilot Chat Component
 * 
 * A floating chat interface that allows users to ask questions about ESG reporting,
 * get help with content generation, and receive guidance on compliance requirements.
 * 
 * AVAILABILITY: Pro plan subscribers only
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Sparkles,
  Bot,
  User,
  Minimize2,
  Maximize2,
  HelpCircle,
  Lock,
  Crown
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Suggested questions - context-aware based on current page
const SUGGESTED_QUESTIONS = {
  dashboard: [
    "How can I reduce my electricity emissions?",
    "What NGA factor applies to my state?",
    "How do I track emissions for a multi-year period?",
  ],
  report_builder: [
    "What should I include in my Board Oversight section?",
    "How do I write a climate transition plan?",
    "What are physical vs transition risks?",
  ],
  general: [
    "What is AASB S2 and do I need to comply?",
    "Explain the ASRS reporting timeline",
    "What's the difference between Scope 1 and Scope 2?",
  ],
};

export default function AICoPilot({ 
  organisationId, 
  organisationName, 
  currentSection,
  subscriptionTier = 'demo', // 'demo', 'standard', 'pro'
  currentPage = 'general' // 'dashboard', 'report_builder', 'general'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi! I'm your ESG Co-Pilot. I can help you with:\n\n• Understanding AASB S2 requirements\n• Interpreting NGA emission factors\n• Explaining climate risks and metrics\n• Questions about your organisation's data\n• General sustainability guidance\n\nHow can I assist you today?`,
      timestamp: new Date().toISOString(),
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check if user has Pro subscription
  const isPro = subscriptionTier === 'pro';

  // Get context-aware suggested questions
  const suggestedQuestions = SUGGESTED_QUESTIONS[currentPage] || SUGGESTED_QUESTIONS.general;

  // Fetch organization data for context
  useEffect(() => {
    if (organisationId && isOpen && !orgData) {
      fetchOrgData();
    }
  }, [organisationId, isOpen]);

  const fetchOrgData = async () => {
    try {
      const [emissionsRes, risksRes, governanceRes, socialRes, offsetsRes, materialityRes] = await Promise.all([
        supabase.from('emissions_data').select('category, total_tco2e, entry_date').eq('organisation_id', organisationId).limit(50),
        supabase.from('climate_risks').select('risk_type, risk_category, likelihood, impact, description').eq('organisation_id', organisationId),
        supabase.from('governance').select('item_type, item_value').eq('organisation_id', organisationId),
        supabase.from('social_metrics').select('*').eq('organisation_id', organisationId).limit(5),
        supabase.from('carbon_offsets').select('offset_type, amount_tco2e, purchase_date').eq('organisation_id', organisationId),
        supabase.from('materiality_assessments').select('topic, stakeholder_importance, business_importance').eq('organisation_id', organisationId),
      ]);

      // Calculate emissions summary
      const emissions = emissionsRes.data || [];
      const totalEmissions = emissions.reduce((sum, e) => sum + (e.total_tco2e || 0), 0);
      const categories = [...new Set(emissions.map(e => e.category))];

      setOrgData({
        emissions_summary: {
          total_tco2e: totalEmissions.toFixed(2),
          entry_count: emissions.length,
          categories: categories.slice(0, 10),
        },
        risks: (risksRes.data || []).slice(0, 10).map(r => ({
          type: r.risk_type,
          category: r.risk_category,
          likelihood: r.likelihood,
          impact: r.impact,
          description: r.description?.substring(0, 100),
        })),
        governance: (governanceRes.data || []).slice(0, 10),
        social: socialRes.data?.[0] || null,
        offsets: {
          total: (offsetsRes.data || []).reduce((sum, o) => sum + (o.amount_tco2e || 0), 0),
          count: (offsetsRes.data || []).length,
        },
        materiality: (materialityRes.data || []).slice(0, 10),
      });
    } catch (err) {
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Send message to AI
  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Get auth session
      const { data: { session } } = await supabase.auth.getSession();

      // Build context for the AI including organization data
      const context = {
        organisation_name: organisationName,
        current_section: currentSection,
        question: messageText,
        conversation_history: messages.slice(-4).map(m => ({
          role: m.role,
          content: m.content.substring(0, 500) // Limit history length
        })),
        // Include organization data for context-aware responses
        organisation_data: orgData ? {
          emissions: orgData.emissions_summary,
          climate_risks: orgData.risks,
          governance: orgData.governance,
          social_metrics: orgData.social,
          carbon_offsets: orgData.offsets,
          materiality: orgData.materiality,
        } : null,
      };

      // Build a data-aware prompt
      let dataContext = '';
      if (orgData) {
        dataContext = `\n\nOrganisation Data Available:
- Total Emissions: ${orgData.emissions_summary?.total_tco2e || 0} tCO₂e from ${orgData.emissions_summary?.entry_count || 0} entries
- Emission Categories: ${orgData.emissions_summary?.categories?.join(', ') || 'None recorded'}
- Climate Risks Identified: ${orgData.risks?.length || 0} risks
- Governance Items: ${orgData.governance?.length || 0} items
- Carbon Offsets: ${orgData.offsets?.total?.toFixed(2) || 0} tCO₂e from ${orgData.offsets?.count || 0} purchases
- Materiality Topics: ${orgData.materiality?.length || 0} topics assessed

Use this data when relevant to answer the user's question.`;
      }

      // Call the AI Edge Function with a special mode for co-pilot chat
      const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-ai-narrative`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          organisation_id: organisationId || 'chat-session',
          section_slug: 'copilot-chat',
          context_data: context,
          custom_prompt: `You are an ESG Report Co-Pilot assistant helping users create sustainability reports compliant with Australian AASB S2 standards.

IMPORTANT: Always use Australian English spelling (e.g., "organisation" not "organization", "recognised" not "recognized", "colour" not "color", "behaviour" not "behavior", "licence" not "license", "programme" not "program" where appropriate). Use Australian terminology like "financial year" not "fiscal year".

The user is working on a report for "${organisationName || 'their organisation'}"${currentSection ? ` and is currently on the "${currentSection}" section` : ''}.
${dataContext}

Answer their question helpfully and concisely. If they ask about their organisation's data, use the data provided above. If they ask about compliance requirements, be specific about AASB S2 and Australian regulations. If they ask for help writing content, provide professional, compliant suggestions.

User's question: ${messageText}

Provide a helpful, professional response. Keep it concise but informative. If referencing organisation data, be specific with numbers.`,
          temperature: 0.7,
          max_tokens: 800,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.content) {
        const assistantMessage = {
          role: 'assistant',
          content: result.content,
          timestamp: new Date().toISOString(),
          auditId: result.audit_id,
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error(result.error || 'Failed to get response');
      }

    } catch (error) {
      console.error('Co-pilot error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again or rephrase your question.",
        timestamp: new Date().toISOString(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle enter key
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  // Handle suggested question click
  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => {
            if (isPro) {
              setIsOpen(true);
            } else {
              setShowUpgradePrompt(true);
            }
          }}
          className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white hover:shadow-xl hover:scale-105 transition-all ${
            isPro 
              ? 'bg-gradient-to-r from-purple-600 to-indigo-600' 
              : 'bg-gradient-to-r from-slate-400 to-slate-500'
          }`}
          title={isPro ? "AI Co-Pilot" : "AI Co-Pilot (Pro Feature)"}
          data-testid="ai-copilot-button"
        >
          <MessageCircle className="w-6 h-6" />
          {isPro ? (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
          ) : (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full border-2 border-white flex items-center justify-center">
              <Crown className="w-3 h-3 text-white" />
            </span>
          )}
        </button>
      )}

      {/* Upgrade Prompt Modal (for non-Pro users) */}
      {showUpgradePrompt && !isPro && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowUpgradePrompt(false)}>
          <div 
            className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-[#003B5C] mb-2">AI Co-Pilot</h3>
              <p className="text-slate-600 text-sm mb-4">
                Get instant answers about ESG compliance, NGA factors, and your organisation's data with our AI-powered assistant.
              </p>
              <div className="bg-purple-50 rounded-lg p-3 mb-4">
                <p className="text-purple-700 text-sm font-medium">
                  Available on Pro Plan
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowUpgradePrompt(false)}
                >
                  Maybe Later
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600"
                  onClick={() => {
                    setShowUpgradePrompt(false);
                    // Navigate to pricing - you may want to use navigate() here
                    window.location.href = '/pricing';
                  }}
                >
                  Upgrade to Pro
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Window (Pro users only) */}
      {isOpen && isPro && (
        <div 
          className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col transition-all duration-300 ${
            isMinimized 
              ? 'bottom-6 right-6 w-72 h-14' 
              : 'bottom-6 right-6 w-96 h-[500px]'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-t-2xl">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <span className="font-semibold text-sm">AI Co-Pilot</span>
                {!isMinimized && (
                  <span className="text-xs text-white/70 block">ESG Report Assistant</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                {isMinimized ? (
                  <Maximize2 className="w-4 h-4" />
                ) : (
                  <Minimize2 className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-2 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.role === 'assistant' && (
                      <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-purple-600" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        message.role === 'user'
                          ? 'bg-[#003B5C] text-white rounded-br-md'
                          : message.isError
                            ? 'bg-red-50 text-red-700 rounded-bl-md'
                            : 'bg-slate-100 text-slate-700 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-7 h-7 bg-[#003B5C] rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="flex gap-2 justify-start">
                    <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Questions (show only at start) */}
              {messages.length === 1 && (
                <div className="px-4 pb-2">
                  <p className="text-xs text-slate-500 mb-2 flex items-center gap-1">
                    <HelpCircle className="w-3 h-3" />
                    Try asking:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {suggestedQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSuggestedQuestion(q)}
                        className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 px-2.5 py-1.5 rounded-full transition-colors"
                      >
                        {q.length > 35 ? q.substring(0, 35) + '...' : q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-3 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <Input
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask me anything about ESG reporting..."
                    className="flex-1 text-sm"
                    disabled={isLoading}
                  />
                  <Button
                    size="sm"
                    onClick={() => sendMessage(inputValue)}
                    disabled={!inputValue.trim() || isLoading}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:opacity-90"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
